
import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { Truck, Filter, Search, MapPin, Gauge } from 'lucide-react';
import { Link } from 'react-router-dom';

const Inventory: React.FC = () => {
  const { trucks, settings } = useData();
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  // Robust data retrieval
  const safeTrucks = Array.isArray(trucks) ? trucks : [];
  const safeSettings = settings || {};
  const logoColor = safeSettings.logoColor || '#D61F26';

  // Filter and Sort Logic
  const filteredTrucks = safeTrucks.filter(truck => {
    if (!truck) return false;
    const matchesCategory = filter === 'All' || truck.category === filter;
    const searchLower = (search || '').toLowerCase();
    const matchesSearch = (truck.name || '').toLowerCase().includes(searchLower) ||
      (truck.brand || '').toLowerCase().includes(searchLower);
    return matchesCategory && matchesSearch;
  }).sort((a, b) => (a.order || 0) - (b.order || 0));

  const categories = ['All', ...(safeSettings.vehicleCategories || [])];

  // Fallback image constant
  const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&q=80&w=1000';

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = FALLBACK_IMAGE;
    e.currentTarget.onerror = null;
  };

  return (
    <div className="bg-neutral-100 min-h-screen pt-20 md:pt-24 pb-12">
      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setShowCategoryModal(false)}>
          <div
            className="bg-white rounded-sm shadow-2xl w-full max-w-4xl p-8 animate-in fade-in zoom-in duration-200"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
              <h2 className="text-2xl font-bold font-display uppercase text-gray-900">Vehicle Categories</h2>
              <button
                onClick={() => setShowCategoryModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-red-600"
              >
                <Search size={24} className="rotate-45" />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => {
                    setFilter(cat);
                    setShowCategoryModal(false);
                  }}
                  className={`p-4 border rounded-sm text-left transition-all hover:shadow-md group ${filter === cat
                    ? 'border-automax-red bg-red-50 ring-1 ring-automax-red'
                    : 'border-gray-200 hover:border-automax-red bg-white'}`}
                >
                  <span className={`block font-bold text-lg mb-1 ${filter === cat ? 'text-automax-red' : 'text-gray-800 group-hover:text-automax-red'}`}>
                    {cat}
                  </span>
                  <span className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                    {cat === 'All' ? safeTrucks.length : safeTrucks.filter(t => t.category === cat).length} Vehicles
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-neutral-900 text-white py-8 md:py-12 mb-6 md:mb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl md:text-5xl font-bold uppercase tracking-wider">
            Our Inventory
          </h1>
          <p className="text-gray-400 mt-2 text-sm md:text-base">
            Browse the largest selection of commercial trucks in East Africa.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Controls */}
        <div className="bg-white p-4 md:p-6 rounded-sm shadow-sm mb-8 flex flex-col md:flex-row gap-4 md:gap-6 items-start md:items-center justify-between sticky top-[70px] md:top-24 z-30 border border-gray-100 transition-all shadow-md">

          {/* Categories */}
          <div className="w-full md:w-auto overflow-x-auto pb-2 md:pb-0 -mx-4 md:mx-0 px-4 md:px-0 scrollbar-hide flex items-center gap-2">
            <button
              onClick={() => setShowCategoryModal(true)}
              className="bg-neutral-900 text-white px-4 py-2 rounded-sm text-xs md:text-sm font-bold uppercase tracking-wider hover:bg-black transition-colors shrink-0 flex items-center gap-2"
            >
              <Filter size={16} /> All Categories
            </button>
            <div className="h-6 w-px bg-gray-200 mx-2 shrink-0 hidden md:block"></div>
            <div className="flex gap-2 min-w-max">
              {categories.slice(0, 5).map(cat => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  style={{ backgroundColor: filter === cat ? logoColor : '' }}
                  className={`px-4 py-2 rounded-sm text-xs md:text-sm font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${filter === cat
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                >
                  {cat}
                </button>
              ))}
              {categories.length > 5 && (
                <button
                  onClick={() => setShowCategoryModal(true)}
                  className="px-4 py-2 rounded-sm text-xs md:text-sm font-bold uppercase tracking-wider transition-colors whitespace-nowrap bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  + {categories.length - 5} More
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search make, model..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 focus:outline-none rounded-sm bg-gray-50 text-sm focus:border-automax-red transition-colors"
            />
          </div>
        </div>

        {/* Grid - NO FRAMER MOTION */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredTrucks.map(truck => (
            <div
              key={truck.id || Math.random()}
              className="bg-white rounded-sm shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden group flex flex-col h-full"
            >
              {/* Image Area */}
              <Link to={`/inventory/${truck.id}`} className="block relative aspect-[16/10] overflow-hidden bg-gray-200">
                <img
                  src={truck.image || FALLBACK_IMAGE}
                  alt={truck.name || 'Truck'}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  onError={handleImageError}
                />
                {!truck.available && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="bg-red-600 text-white px-4 py-2 font-display font-bold uppercase tracking-widest text-lg md:text-xl rotate-[-15deg] border-2 border-white">Sold Out</span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/80 to-transparent p-4">
                  <span className="text-white font-display text-lg md:text-xl tracking-wide shadow-black drop-shadow-md">{truck.brand}</span>
                </div>
              </Link>

              {/* Content */}
              <div className="p-4 md:p-6 flex flex-col flex-grow">
                <Link to={`/inventory/${truck.id}`} className="block mb-2">
                  <h3 className="font-bold text-gray-900 text-base md:text-lg leading-tight line-clamp-2 hover:text-automax-red transition-colors min-h-[3rem]">{truck.name}</h3>
                </Link>

                <div className="grid grid-cols-2 gap-y-2 text-xs md:text-sm text-gray-600 mb-4 md:mb-6">
                  {!truck.category?.toLowerCase().includes('trailer') && (
                    <>
                      <div className="flex items-center"><Gauge size={14} className="mr-2" style={{ color: logoColor }} /> {truck.maxSpeed}</div>
                      <div className="flex items-center"><Truck size={14} className="mr-2" style={{ color: logoColor }} /> {truck.horsepower} HP</div>
                    </>
                  )}
                  <div className="flex items-center"><MapPin size={14} className="mr-2" style={{ color: logoColor }} /> {truck.location}</div>
                  <div className="flex items-center font-bold" style={{ color: logoColor }}>{truck.category}</div>
                </div>

                {/* Footer */}
                <div className="mt-auto flex items-center justify-between border-t border-gray-100 pt-4">
                  <div className="flex-1"></div>
                  <Link
                    to={`/inventory/${truck.id}`}
                    className="bg-neutral-900 text-white px-4 md:px-5 py-2 rounded-sm font-bold text-xs md:text-sm uppercase transition-colors hover:bg-automax-red flex items-center"
                  >
                    Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredTrucks.length === 0 && (
          <div className="text-center py-24">
            <Filter size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-bold text-gray-500">No trucks found matching your criteria.</h3>
            <button
              onClick={() => { setFilter('All'); setSearch('') }}
              className="mt-4 font-bold hover:underline"
              style={{ color: logoColor }}
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>


    </div>
  );
};

export default Inventory;
